import { Clear, PushPin, PushPinOutlined } from "@mui/icons-material"
import {
    Avatar,
    Divider,
    IconButton,
    Typography,
    useTheme,
} from "@mui/material"
import { ReactElement } from "react"

import { IComment } from "../../../services/middleware/mappers/singleDeviceMapper"
import { formatDateTime } from "../../../utils/general/generalUtils"
import AppTooltip from "../../helper/appTooltip"

export const CommentDivider = (): ReactElement => (
    <Divider sx={{ marginBottom: 3, marginTop: 2 }} />
)

interface CommentDisplayComponentProps {
    comment: IComment
    isLoading: boolean
    deleteComment: () => void
    togglePin?: () => void
    canPinComment: boolean
    canDeleteComment?: boolean
    isPinned?: boolean
}

/**
 * Displays individual comment including delete and pin buttons.
 * Takes the following inputs:
 *  - comment: Comment to display
 *  - isLoading: Disables buttons when true
 *  - deleteComment: Function called when delete button is pressed
 *  - togglePin: Function called when pin button is pressed
 *  - canPinComment: Used to determine if user can pin comments
 *  - isPinned: Determines styling of component
 */
const CommentDisplayComponent = ({
    comment,
    isLoading,
    deleteComment,
    togglePin: pinOrUnpinComment,
    canPinComment,
    canDeleteComment = true,
    isPinned,
}: CommentDisplayComponentProps): ReactElement => {
    const theme = useTheme()
    const name =
        comment.firstName && comment.lastName
            ? `${comment.firstName} ${comment.lastName}`
            : "Unknown"
    const initials = comment.initials ? comment.initials : "UU"

    const displayGroup = comment.groupType ?? null

    return (
        <div
            data-testid="comment"
            style={
                isPinned
                    ? {
                          backgroundColor:
                              theme.palette.background.contentBackground,
                          paddingLeft: "8px",
                          paddingRight: "8px",
                          paddingTop: "16px",
                          paddingBottom: "16px",
                          marginLeft: "-8px",
                          marginRight: "-8px",
                          marginBottom: "32px",
                          borderRadius: "8px",
                      }
                    : {}
            }
        >
            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    flex: 1,
                    gap: "4px",
                }}
            >
                <Avatar
                    style={{ marginRight: "8px" }}
                    sx={{
                        bgcolor: comment.fromRequestor
                            ? theme.palette.secondary.main
                            : theme.palette.primary.main,
                    }}
                >
                    {initials}
                </Avatar>
                <div style={{ flex: 1 }}>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "4px",
                            justifyItems: "start",
                        }}
                    >
                        <div>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    gap: "4px",
                                }}
                            >
                                <Typography variant="body2" fontWeight="bold">
                                    {name}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    {displayGroup && `• ${displayGroup}`}
                                </Typography>
                            </div>
                            <Typography
                                variant="body2"
                                fontStyle="italic"
                                color="textSecondary"
                            >
                                {formatDateTime(comment.publishedAt)}
                            </Typography>
                        </div>
                        <div
                            style={{ marginLeft: "auto", marginBottom: "auto" }}
                        >
                            {canPinComment && (
                                <AppTooltip
                                    title={
                                        isPinned
                                            ? "Unpin comment"
                                            : "Pin comment"
                                    }
                                >
                                    <IconButton
                                        disabled={isLoading}
                                        onClick={pinOrUnpinComment}
                                    >
                                        {isPinned ? (
                                            <PushPin />
                                        ) : (
                                            <PushPinOutlined />
                                        )}
                                    </IconButton>
                                </AppTooltip>
                            )}
                            {canDeleteComment && (
                                <AppTooltip title="Delete comment">
                                    <IconButton
                                        onClick={deleteComment}
                                        disabled={isLoading}
                                    >
                                        <Clear />
                                    </IconButton>
                                </AppTooltip>
                            )}
                        </div>
                    </div>
                    <Typography variant="body1" style={{ marginTop: "4px" }}>
                        {comment.comment}
                    </Typography>
                </div>
            </div>
            {!isPinned && <CommentDivider />}
        </div>
    )
}

CommentDisplayComponent.defaultProps = {
    isPinned: false,
    togglePin: undefined,
    canDeleteComment: true,
}

export default CommentDisplayComponent
